import React from "react";
import { HtmlHTMLAttributes, MouseEvent } from "react";

import { DateTime } from "luxon";

import { formatDateLocal, formatDateLocalNoYear } from "../Utilities";

import "../button.css";
import "./Calendar.css";

interface ICalendar2Props extends HtmlHTMLAttributes<HTMLElement> {
    displayMonth: string;
    highlightDate?: string | Date;
    highlightDateStart?: string | Date;
    highlightDateEnd?: string | Date;
    events?: { [day: number]: any[] };
    showDetailsOnClick?: boolean;
    showButtonsOnDetails?: boolean;
    allowAdd?: boolean;
    allowEdit?: boolean;
    disableDelete?: boolean;
    selectionMode?: "day" | "week";
    editorClass?: string;
    onDaySelected?: (date: string) => void;
    onDisplayRangeChanged?: (minDate: string, maxDate: string) => void;
    onBeforeShowAdd?: () => PromiseLike<void>;
    onBeforeEdit?: (item: any) => PromiseLike<boolean>
    onItemAdd?: (date: string, button: string) => PromiseLike<void>;
    onItemEdit?: (date: string, button: string) => PromiseLike<void>;
    onItemDelete?: (date: string, button: string) => PromiseLike<void>;

    detailsAddRender?: (state: any) => React.ReactChild;
    detailsButtonsRender?: (cb: ICalendar2ButtonCallbacks) => React.ReactChild[];
    detailsItemRender?: (item: any) => React.ReactChild;
    dayDetailsButtonsRender?: (info: ICalendarDetailsInfo) => React.ReactChild[];
}

type DayDetailsInfo = {
    date: DateTime;
    left: string | number;
    top: string | number;

    dateStart: DateTime;
    dateEnd: DateTime;
};

interface ICalendar2State {
    dialogLoading: boolean | string;

    dayDetails?: DayDetailsInfo;
    isDetailsVisible: boolean;
    isDetailsAdd: boolean;
    isDetailsEdit: boolean;

    editItem?: any;
}

export interface ICalendar2ButtonCallbacks {
    isEdit: boolean;
    save: (e?: MouseEvent<HTMLButtonElement>) => void;
    delete: (e?: MouseEvent<HTMLButtonElement>) => void;
    close: () => void;
}

export interface ICalendarDetailsInfo {
    selectedDateStart: DateTime;
    selectedDateEnd: DateTime;
}

class Calendar2 extends React.Component<ICalendar2Props, ICalendar2State> {
    private _buttonCallbacks: ICalendar2ButtonCallbacks;

    constructor(props: Readonly<ICalendar2Props>) {
        super(props);

        this.state = {
            dialogLoading: false,

            isDetailsVisible: false,
            isDetailsAdd: false,
            isDetailsEdit: false,
        };

        this.handleDayClick = this.handleDayClick.bind(this);
        this.closeDayDetails = this.closeDayDetails.bind(this);
        this.handleOnAdd = this.handleOnAdd.bind(this);
        this.handleOnEdit = this.handleOnEdit.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleEditItem = this.handleEditItem.bind(this);
        this.handleDelete = this.handleDelete.bind(this);

        this._buttonCallbacks = {
            isEdit: false,
            close: this.closeDayDetails,
            delete: e => {
                this.handleDelete(e ? e.currentTarget.name : "")
            },
            save: e => this.handleSave(e)
        };

        this.calculateDisplayedRange();
    }

    public componentDidUpdate(prevProps: Readonly<ICalendar2Props>) {
        if (this.props.displayMonth !== prevProps.displayMonth) {
            this.calculateDisplayedRange();
        }
    }

    public render() {
        if (!this.props.displayMonth) {
            return (
                <div className={"calendar " + this.props.className}>
                    <header>
                        <div className="day weekend">Sun</div>
                        <div className="day">Mon</div>
                        <div className="day">Tues</div>
                        <div className="day">Wed</div>
                        <div className="day">Thu</div>
                        <div className="day">Fri</div>
                        <div className="day weekend">Sat</div>
                    </header>
                    <div className="calendar-inner" />
                </div>
            );
        }

        const month = DateTime.fromISO(this.props.displayMonth);
        const dayOffset = (month.weekday % 7); // Day of week
        const daysInMonth = month.daysInMonth;

        let highlightStart;
        let highlightEnd;
        if (this.props.highlightDate) {
            const date = typeof this.props.highlightDate === "string"
                ? DateTime.fromISO(this.props.highlightDate)
                : DateTime.fromJSDate(this.props.highlightDate);

            highlightStart = date.startOf("day");
            highlightEnd = date.startOf("day");
        }
        else if (this.props.highlightDateStart && this.props.highlightDateEnd) {
            highlightStart = (typeof this.props.highlightDateStart === "string"
                ? DateTime.fromISO(this.props.highlightDateStart)
                : DateTime.fromJSDate(this.props.highlightDateStart)).startOf("day");

            highlightEnd = (typeof this.props.highlightDateEnd === "string"
                ? DateTime.fromISO(this.props.highlightDateEnd)
                : DateTime.fromJSDate(this.props.highlightDateEnd)).startOf("day");
        }

        // One row per week
        const mainOut = [];
        let weekOut = [];
        let weekItems = [];
        const displaySlots: Array<{ counter: number }> = [];
        for (let i = 0; i <= 42 /* 6 weeks display */; i++) {
            if (i > 0 && (i % 7) === 0) {
                mainOut.push(<div key={i / 7} className={"week" + (this.props.selectionMode === "week" ? " week-select" : "")}>
                    {weekOut}
                    <div className="items-area-root">
                        <div className="items-area">{weekItems}</div>
                    </div>
                </div>);
                weekOut = [];
                weekItems = [];
                displaySlots.splice(0);

                if (i === 42) {
                    break;
                }
            }

            const monthDay = (i - dayOffset) + 1;
            const isMonthDay = monthDay > 0 && monthDay <= daysInMonth;
            const dayEvents = (this.props.events) ? this.props.events[monthDay] : null;
            const newMonth = month.plus({ day: monthDay - 1 });
            const monthDayDisplay = newMonth.day;

            // Tick away slot claims
            for (let p = 0; p < displaySlots.length; p++) {
                if (displaySlots[p].counter > 0) {
                    displaySlots[p].counter--;
                }
            }

            if (dayEvents) {
                for (const e of dayEvents) {
                    const len = Math.max(e.length, 1) || 1;
                    let top = displaySlots.findIndex(a => a.counter === 0);
                    if (top === -1) {
                        top = displaySlots.length;
                    }
                    weekItems.push(<div key={e.id || (monthDay.toFixed(0) + e.title)} className={`cal-item ${e.type ? `cal-event-${e.type}` : ""}`} style={{ width: `calc(${14.285 * len}% - 4px)`, left: `${14.285 * (i % 7)}%`, top: `${1.5 * top}em` }}>{e.title}</div>);

                    if (top === displaySlots.length) {
                        displaySlots.push({ counter: len });
                    }
                    else {
                        displaySlots[top].counter = len;
                    }
                }
            }

            const isDayHighlight = highlightStart && highlightEnd && newMonth.diff(highlightStart, "days").days >= 0 && newMonth.diff(highlightEnd, "days").days <= 0;

            weekOut.push((
                <div key={monthDay} data-date={monthDay} onClick={this.handleDayClick} className={"day" + (isDayHighlight ? " today" : "") + (!isMonthDay ? " spacer" : "")}>
                    <span>{monthDayDisplay}</span>
                </div>
            ));
        }

        let dayDetails;
        if (this.state.isDetailsVisible && this.state.dayDetails) {
            let dayEventIndex = this.state.dayDetails.dateStart.diff(month, "days").days + 1;
            let dayEventIndexEnd = this.state.dayDetails.dateEnd.diff(month, "days").days + 1;

            // Find all events that start on or cross over the selected day
            // Or if in week mode, any items from the whole week
            const allDayEvents: any[] = [];
            if (this.props.events) {
                for (const item of Object.keys(this.props.events)) {
                    if (+item > dayEventIndexEnd) {
                        continue;
                    }

                    for (const evt of this.props.events[+item]) {
                        if (+item + evt.length > dayEventIndex) {
                            if (evt.group === undefined || !allDayEvents.some(v => v.group === evt.group)) {
                                evt.extendsBefore = +item < dayEventIndex;
                                evt.extendsAfter = +item + evt.length > dayEventIndex + 1;
                                allDayEvents.push(evt);
                            }
                        }
                    }
                }
            }

            if (this.state.isDetailsAdd) {
                this._buttonCallbacks.isEdit = this.state.isDetailsEdit;
                dayDetails = (
                    <div className="day-details-scrim">
                        <div className={"day-details-large" + (this.props.editorClass ? ` ${this.props.editorClass}` : "")}>
                            <header>
                                {
                                    this.props.selectionMode === "week"
                                        ? <b>{this.state.isDetailsEdit ? "Edit" : "Create"} schedule - {formatDateLocal(this.state.dayDetails.dateStart, true)} - {formatDateLocal(this.state.dayDetails.dateEnd, true)}</b>
                                        : <b>{this.state.isDetailsEdit ? "Edit" : "Create"} schedule - {formatDateLocal(this.state.dayDetails.date)}</b>
                                }
                                <button className="button btn-light small btn-center" onClick={this.closeDayDetails}><i className="icon-times" /></button>
                            </header>
                            <div className="day-details-body">
                                {
                                    this.props.detailsAddRender
                                        ? this.props.detailsAddRender({
                                            isEdit: this.state.isDetailsEdit
                                        })
                                        : undefined
                                }
                            </div>
                            {
                                this.state.dialogLoading === true
                                    ? <div className="spinner lds-dual-ring" />
                                    : (typeof this.state.dialogLoading) === "string"
                                        ? <span className="error-text">{this.state.dialogLoading}</span>
                                        : ""
                            }
                            {
                                this.props.detailsButtonsRender
                                    ? <div className="day-details-buttons-large">
                                        {this.props.detailsButtonsRender(this._buttonCallbacks)}
                                    </div>
                                    : <div className="day-details-buttons-large">
                                        {
                                            this.state.isDetailsEdit && !this.props.disableDelete
                                                ? <div className="control-group">
                                                    <button className="button btn-light red text-with-icon" onClick={() => this.handleDelete("full")}><i className="icon-trash" /><span>Delete full</span></button>
                                                    <button className="button btn-light red text-with-icon" onClick={() => this.handleDelete("single")}><i className="icon-trash" /><span>Delete day</span></button>
                                                </div>
                                                : undefined
                                        }
                                        <button className="button text-with-icon push-right" onClick={this.handleSave}><i className="icon-check" /><span>Save</span></button>
                                    </div>
                            }
                        </div>
                    </div>
                );
            }
            else {
                dayDetails = (
                    <div className="day-details" style={{ top: `calc(16.67% * ${this.state.dayDetails.top} - 1em - 2px)`, left: `calc(14.285% * ${this.state.dayDetails.left} - 1em)` }}>
                        <header>
                            {
                                this.props.selectionMode === "week"
                                    ? <b>{formatDateLocalNoYear(this.state.dayDetails.dateStart, true)} - {formatDateLocal(this.state.dayDetails.dateEnd, true)}</b>
                                    : <b>{formatDateLocal(this.state.dayDetails.date)}</b>
                            }
                            <button className="button btn-light small btn-center" onClick={this.closeDayDetails}><i className="icon-times" /></button>
                        </header>
                        <div className="day-details-body">
                            {
                                allDayEvents.length > 0
                                    ? <ul className="day-details-list">
                                        {
                                            allDayEvents.map(e => (
                                                <div key={e.title} className={`day-details-list-item`/* ${e.extendsBefore ? " cal-item-before" : ""}${e.extendsAfter ? " cal-item-after" : ""}`*/}>
                                                    {/*e.extendsBefore ? <div className="cal-item-before-arrow" /> : undefined*/}
                                                    {
                                                        this.props.allowEdit
                                                            ? <button className={`cal-item ${e.type ? `cal-event-${e.type}` : ""}`} onClick={() => this.handleEditItem(e)}>{this.props.detailsItemRender ? this.props.detailsItemRender(e) : e.title}</button>
                                                            : <div className={`cal-item ${e.type ? `cal-event-${e.type}` : ""}`}>{this.props.detailsItemRender ? this.props.detailsItemRender(e) : e.title}</div>
                                                    }
                                                    {/*e.extendsAfter ? <div className="cal-item-after-arrow" /> : undefined*/}
                                                </div>
                                            ))
                                        }
                                    </ul>
                                    : <em><small>No events this day</small></em>
                            }
                        </div>
                        {
                            this.props.showButtonsOnDetails
                                ? <div className="day-details-buttons">
                                    {this.props.dayDetailsButtonsRender ? this.props.dayDetailsButtonsRender({ selectedDateStart: this.state.dayDetails.dateStart, selectedDateEnd: this.state.dayDetails.dateEnd }) : null}
                                    <span className="spring" />
                                    {this.props.allowAdd ? <button className="button small btn-light btn-center" onClick={this.handleOnAdd}><i className="icon-plus" /></button> : undefined}
                                </div>
                                : undefined
                        }
                    </div>
                );
            }
        }

        return (
            <div className={"calendar " + this.props.className}>
                <header>
                    <div className="day weekend">Sun</div>
                    <div className="day">Mon</div>
                    <div className="day">Tues</div>
                    <div className="day">Wed</div>
                    <div className="day">Thu</div>
                    <div className="day">Fri</div>
                    <div className="day weekend">Sat</div>
                </header>
                <div className="calendar-inner">
                    {mainOut}
                    {dayDetails}
                </div>
            </div>
        );
    }

    private handleDayClick(e: MouseEvent<HTMLDivElement>) {
        if (!this.props.onDaySelected && !this.props.showDetailsOnClick) {
            return;
        }

        const dateAttr = e.currentTarget.attributes.getNamedItem("data-date");
        if (!dateAttr) {
            return;
        }

        const dateNumber = parseInt(dateAttr.value, 10);
        const date = DateTime.fromISO(this.props.displayMonth).plus({ day: dateNumber - 1 });

        if (this.props.showDetailsOnClick) {
            this.showDayDetails(date);
        }

        if (this.props.onDaySelected) {
            this.props.onDaySelected(date.toISODate());
        }
    }

    private calculateDisplayedRange() {
        if (!this.props.displayMonth) {
            return;
        }

        const month = DateTime.fromISO(this.props.displayMonth);
        const dayOffset = month.weekday % 7; // Day of week
        const daysInMonth = month.daysInMonth;
        const extraDays = 42 - dayOffset - daysInMonth;

        const minDate = month.plus({ days: -dayOffset });
        const maxDate = month.endOf("month").plus({ days: extraDays });

        if (this.props.onDisplayRangeChanged) {
            this.props.onDisplayRangeChanged(minDate.toISODate(), maxDate.toISODate());
        }
    }

    private async showDayDetails(date: DateTime) {
        const month = DateTime.fromISO(this.props.displayMonth);
        const minDate = month.plus({ days: -(month.weekday % 7) });
        const dayDiff = Math.floor(date.diff(minDate, "days").days / 7);

        let left: string | number = Math.min(date.weekday % 7, 5);
        let top: string | number = Math.min(dayDiff, 4);

        if (window.matchMedia("only screen and (min-width: 800px), only print").matches) {
            // Big enough screen to show popup
            if (left === 0) {
                left = `${left} + 1em`;
            }
            else if (left === 5) {
                left = `${left} - 1em`;
            }
            if (top === 4) {
                top = `${3} - 0em`;
            }
        }
        else {
            // Small screen, show popup over whole calendar
            left = "0 + 1em";
            top = "0 + 1em";
        }

        if (this.props.onBeforeShowAdd) {
            await this.props.onBeforeShowAdd();
        }

        this.setState({
            isDetailsVisible: true,
            isDetailsAdd: false,

            dayDetails: {
                date,
                left,
                top,

                dateStart: this.props.selectionMode === "week" ? date.minus({ days: date.weekday % 7 }) : date,
                dateEnd: this.props.selectionMode === "week" ? date.plus({ days: 7 - (date.weekday % 7) - 1 }) : date,
            }
        });
    }

    private closeDayDetails() {
        this.setState({
            isDetailsVisible: false,
            isDetailsAdd: false,
            isDetailsEdit: false,
        });
    }

    private handleOnAdd() {
        this.setState({
            isDetailsAdd: true
        });
    }

    private handleOnEdit() {
        this.setState({
            isDetailsEdit: true
        });
    }

    private async handleSave(e?: MouseEvent<HTMLButtonElement>) {
        if (this.state.dayDetails) {
            try {
                this.setState({
                    dialogLoading: true
                });

                if (this.state.isDetailsEdit) {
                    if (this.props.onItemEdit) {
                        await this.props.onItemEdit(this.state.dayDetails.date.toISODate(), e ? e.currentTarget.name : "");
                    }
                }
                else {
                    if (this.props.onItemAdd) {
                        await this.props.onItemAdd(this.state.dayDetails.date.toISODate(), e ? e.currentTarget.name : "");
                    }
                }

                this.setState({
                    dialogLoading: false
                });
            }
            catch (e) {
                this.setState({
                    dialogLoading: e.message || e || "Failed to submit"
                })
                return;
            }
        }

        this.setState({
            isDetailsAdd: false,
            isDetailsEdit: false,
            isDetailsVisible: false,
        });
    }

    private async handleEditItem(e: any) {
        if (this.props.onBeforeEdit && !(await this.props.onBeforeEdit(e))) {
            return;
        }

        this.setState({
            editItem: e,
            isDetailsEdit: true,
        });

        this.handleOnAdd();
    }

    private async handleDelete(mode: string) {
        if (!this.state.dayDetails) {
            return;
        }

        try {
            this.setState({
                dialogLoading: true
            });

            if (this.props.onItemDelete) {
                await this.props.onItemDelete(this.state.dayDetails.date.toISODate(), mode);
            }

            this.setState({
                dialogLoading: false
            });
        }
        catch (e) {
            this.setState({
                dialogLoading: e.message || e || "Failed to submit"
            })
            return;
        }

        this.setState({
            isDetailsAdd: false,
            isDetailsEdit: false,
            isDetailsVisible: false,
        });
    }
}

export default Calendar2;
